/**
 * Promo code capture and persistence.
 *
 * Codes used to be shareable only as /join/CODE, which drops a first-time visitor
 * straight onto the pricing page — no context, no product, and a bounce rate to
 * match. The point of this helper is that a code can ride on ANY entry point
 * (`?promo=CODE` on the homepage, a feature page, or an external landing page) and
 * still be there at checkout, however long the route between them.
 *
 * The URL alone cannot do that job: `promo` is a NAVIGATION_SCOPED_QUERY_KEY, so
 * helpers/navigationQuery.js deliberately strips it on the next navigation. That is
 * correct for account-flow state and wrong for attribution, so rather than loosen
 * that rule and leave the code trailing through every URL, the code is captured once
 * and kept in localStorage.
 *
 * Persisted rather than sessionStorage because the journey crosses a full page load:
 * signup, email confirmation, and the round trip to Stripe Checkout.
 */

const STORAGE_KEY = "rmePromoCode";

// Campaign-length, not forever. Without this, a code picked up months ago silently
// discounts an unrelated purchase, and nobody would ever work out why.
const TTL_DAYS = 30;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;

// Stripe promotion codes are short and alphanumeric. This is a sanity filter, not
// validation — the code is checked against Stripe server-side at checkout, which is
// the only place that can actually say whether it is real.
const VALID_CODE = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * @param {unknown} code
 * @returns {string|null} The trimmed code, or null if it is not plausibly one.
 */
export function normalizePromoCode(code) {
  if (typeof code !== "string") return null;
  const trimmed = code.trim();
  return VALID_CODE.test(trimmed) ? trimmed : null;
}

/**
 * Remember a code for later. Silently ignores anything malformed, and no-ops when
 * storage is unavailable (Safari private mode, embedded webviews).
 *
 * @param {unknown} code
 * @returns {string|null} The code stored, or null.
 */
export function storePromoCode(code) {
  const normalized = normalizePromoCode(code);
  if (!normalized) return null;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ code: normalized, capturedAt: Date.now() })
    );
  } catch (e) {
    // Storage denied — the code still works for this navigation, just not later.
  }
  return normalized;
}

/**
 * @returns {string|null} A previously captured code, if it has not expired.
 */
export function getStoredPromoCode() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const { code, capturedAt } = JSON.parse(raw);
    if (!capturedAt || Date.now() - capturedAt > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return normalizePromoCode(code);
  } catch (e) {
    return null;
  }
}

export function clearStoredPromoCode() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {}
}

// Handoff from the marketing site. localStorage is per-ORIGIN, so a code captured on
// landing.reviewmyelearning.com is invisible here — but a cookie scoped to the parent
// domain is not. The landing app sets this once and every link across to the product
// carries the campaign, including the ones nobody remembered to decorate.
const COOKIE_NAME = "rme_promo";

/** @returns {string|null} A promo code handed over by a sibling subdomain. */
function readPromoCookie() {
  try {
    const match = document.cookie.match(
      new RegExp("(?:^|;\\s*)" + COOKIE_NAME + "=([^;]*)")
    );
    return match ? normalizePromoCode(decodeURIComponent(match[1])) : null;
  } catch (e) {
    return null;
  }
}

/**
 * Capture a code from a route, if it carries one. Called on every navigation, so a
 * code arriving on any page is kept regardless of where the visitor entered.
 *
 * @param {import('vue-router').RouteLocationNormalized} route
 * @returns {string|null} The code captured on THIS navigation, or null.
 */
export function capturePromoFromRoute(route) {
  // /join/:promo is the original form and still works — plenty of it is already
  // printed on things nobody can recall.
  const explicit =
    storePromoCode(route?.params?.promo) || storePromoCode(route?.query?.promo);
  if (explicit) return explicit;

  // The cookie is the weakest source: it only fills a gap. A visitor who arrived with
  // an explicit code must not have it overwritten on the next navigation by a cookie
  // left behind from an earlier campaign.
  if (!getStoredPromoCode()) {
    const fromCookie = readPromoCookie();
    if (fromCookie) return storePromoCode(fromCookie);
  }
  return null;
}

/**
 * The code that should apply right now: this route's, else the one we remembered.
 *
 * @param {import('vue-router').RouteLocationNormalized} route
 * @returns {string|null}
 */
export function resolvePromoCode(route) {
  return (
    normalizePromoCode(route?.params?.promo) ||
    normalizePromoCode(route?.query?.promo) ||
    getStoredPromoCode()
  );
}

export { STORAGE_KEY as PROMO_STORAGE_KEY, TTL_DAYS as PROMO_TTL_DAYS };
